import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  injectMutation,
  injectQueryClient,
} from '@tanstack/angular-query-experimental';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { lastValueFrom } from 'rxjs';

import {
  DeleteService,
  DuplicateService,
  GetService,
  WorkflowService,
} from 'workflow-definition/openapi';
import {
  DeleteActionMutation,
  DeleteSegmentMutation,
  OptionsData,
  DuplicateInputMutation,
  DuplicateSegmentMutation,
  DuplicateWorkflowMutation,
} from '../../models';
import { CurrentWorkflowService } from '../current-workflow/current-workflow.service';
import { TemplateTransferService } from '../template-transfer/template-transfer.service';
import {
  buildTemplateFileName,
  downloadBlob,
  extractBlobErrorMessage,
} from '../../utils';

@Injectable({
  providedIn: 'root',
})
export class WorkflowBuilderService {
  public _messageService = signal<MessageService | undefined>(undefined);

  public queryClient = injectQueryClient();

  deleteAction = injectMutation(() => ({
    mutationFn: ({ workflowId, stepId, actionId }: DeleteActionMutation) =>
      lastValueFrom(
        this.deleteService.apiV1WorkflowWorkflowIdStepStepRefIdActionActionRefIdDelete(
          workflowId,
          stepId,
          actionId,
          {
            isRecursive: true,
          }
        )
      ),
    onSuccess: () => {
      this.currentWorkflowService.renderRightSidebar(null);
      this.messageService?.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Action deleted successfully.',
      });
    },
    onError: error => {
      this.messageService?.add({
        severity: 'error',
        summary: 'Error',
        detail: error.message,
      });
    },
    onSettled: (_, __, variables) => {
      this.queryClient.invalidateQueries({
        queryKey: ['Segment', variables.segmentId],
      });

      this.queryClient.invalidateQueries({
        queryKey: ['Step', variables.stepId],
      });
    },
  }));

  deleteSegmentMutation = injectMutation(() => ({
    mutationFn: ({ workflowId, segmentRefId }: DeleteSegmentMutation) =>
      lastValueFrom(
        this.deleteService.apiV1WorkflowWorkflowIdSegmentSegmentRefIdDelete(
          workflowId,
          segmentRefId,
          {
            isRecursive: true,
          }
        )
      ),
    onError: error => {
      this.messageService?.add({
        severity: 'error',
        summary: 'Error',
        detail: error.message,
      });
    },
    onSuccess: (_, variables) => {
      this.messageService?.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Segment deleted successfully.',
      });
      this.router.navigate(
        ['workflow', variables.workflowId, 'builder'],
        this.currentWorkflowService.isPreviewMode
          ? { queryParams: { preview: 'true' } }
          : {}
      );
      this.currentWorkflowService.setCurrentSegmentId(null);
      this.currentWorkflowService.setSegmentPropertiesSidebarVisible(false);
      this.currentWorkflowService.setStepPropertiesSidebarVisible(false);
      this.currentWorkflowService.setActionPropertiesSidebarVisible(false);
    },
    onSettled: (_, __, variables) => {
      this.queryClient.invalidateQueries({
        queryKey: ['Workflow', variables.workflowId],
      });
    },
  }));

  duplicateInputMutation = injectMutation(() => ({
    mutationFn: ({
      workflowId,
      actionId,
      stepId,
      duplicateInputRequest,
    }: DuplicateInputMutation) =>
      lastValueFrom(
        this.duplicateService.apiV1WorkflowWorkflowIdDuplicateStepStepRefIdActionActionRefIdPut(
          workflowId,
          actionId,
          stepId,
          duplicateInputRequest
        )
      ),
    onSuccess: (_, variables) => {
      const name = variables.duplicateInputRequest.newName;
      const toTemplate = variables.duplicateInputRequest.toTemplate;
      this.messageService?.add({
        severity: 'success',
        summary: 'Success',
        detail: this.translate
          .instant(
            `success-messages.workflow-builder.${toTemplate ? 'type-template' : 'type-duplicate-template'}`
          )
          .replace('$type', name),
      });
    },
    onError: error => {
      this.messageService?.add({
        severity: 'error',
        summary: 'Error',
        detail: error.message,
      });
    },
    onSettled: (_, __, variables) => {
      this.queryClient.invalidateQueries({
        queryKey: ['Workflow', variables.workflowId],
      });
      this.queryClient.invalidateQueries({
        queryKey: ['Segment', variables.segmentId],
      });
      if (variables.duplicateInputRequest.toTemplate) {
        this.queryClient.invalidateQueries({
          queryKey: ['ActionTemplates'],
        });
      }
    },
  }));

  public duplicateSegmentMutation = injectMutation(() => ({
    mutationFn: ({
      workflowId,
      segmentId,
      duplicateSegmentInputRequest,
    }: DuplicateSegmentMutation) =>
      lastValueFrom(
        this.duplicateService.apiV1WorkflowWorkflowIdDuplicateSegmentSegmentRefIdPut(
          workflowId,
          segmentId,
          duplicateSegmentInputRequest
        )
      ),
    onSuccess: (_, variables) => {
      const name = variables.duplicateSegmentInputRequest.newName;
      const toTemplate = variables.duplicateSegmentInputRequest.toTemplate;
      this.messageService?.add({
        severity: 'success',
        summary: 'Success',
        detail: this.translate
          .instant(
            `success-messages.workflow-builder.${toTemplate ? 'type-template' : 'type-duplicate-template'}`
          )
          .replace('$type', name),
      });
      this.queryClient.invalidateQueries({
        queryKey: ['Workflow', variables.workflowId],
      });
      if (toTemplate) {
        this.queryClient.invalidateQueries({
          queryKey: ['SegmentTemplates'],
        });
      }
    },
    onError: error => {
      this.messageService?.add({
        severity: 'error',
        summary: 'Error',
        detail: error.message,
      });
    },
  }));

  public saveWorkflowGlobalTemplate() {
    return injectMutation(() => ({
      mutationFn: ({
        workflowId,
        duplicateWorkflowInputRequest,
      }: DuplicateWorkflowMutation) =>
        lastValueFrom(
          this.workflowService.apiV1WorkflowDuplicateWorkflowIdPost(
            workflowId,
            duplicateWorkflowInputRequest
          )
        ),
      onSuccess: (_, variables) => {
        this.messageService?.add({
          severity: 'success',
          summary: 'Success',
          detail: `Workflow saved as ${variables.duplicateWorkflowInputRequest.customerId ? 'Template' : 'Global Template'} successfully.`,
        });
      },
      onError: error => {
        this.messageService?.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message,
        });
      },
      onSettled: (_, __, variables) => {
        this.queryClient.invalidateQueries({
          queryKey: ['Workflow', variables.workflowId],
        });
        this.queryClient.invalidateQueries({
          queryKey: ['SegmentTemplates'],
        });
      },
    }));
  }

  constructor(
    private currentWorkflowService: CurrentWorkflowService,
    private deleteService: DeleteService,
    private duplicateService: DuplicateService,
    private router: Router,
    private translate: TranslateService,
    private workflowService: WorkflowService,
    private getService: GetService,
    private templateTransfer: TemplateTransferService
  ) {}

  setMessageService(val: MessageService | undefined) {
    this._messageService.set(val);
  }

  get messageService() {
    return this._messageService();
  }

  public async handleActionDropdownChange(
    workflowId: number | undefined,
    segmentId: number | undefined,
    stepId: number | undefined,
    actionId: number | undefined,
    option: OptionsData<string> | null,
    newName: string | null
  ) {
    if (!workflowId || !segmentId || !stepId || !actionId || actionId === -1)
      return;

    if (option?.value === 'duplicate' || option?.value === 'saveTemplate') {
      const division = this.currentWorkflowService.currentDivision.getValue();

      // customerId is not sent: the API ignores it on both paths (nulled when toTemplate is false,
      // overwritten from the JWT when true), and "" cannot bind to the server's Guid?. The session
      // therefore decides the scope - a global admin's save produces a global template.
      const duplicateInputMutation: DuplicateInputMutation = {
        workflowId,
        segmentId,
        stepId,
        actionId,
        duplicateInputRequest: {
          newName,
          toTemplate: option?.value !== 'duplicate',
          division,
        },
      };

      if (this.duplicateInputMutation.isPending()) return;
      this.duplicateInputMutation.mutate(duplicateInputMutation);
    } else if (option?.value === 'delete') {
      if (this.deleteAction.isPending()) return;
      const deleteAction: DeleteActionMutation = {
        workflowId,
        segmentId,
        stepId,
        actionId,
      };
      this.deleteAction.mutate(deleteAction);
    } else if (option?.value === 'export') {
      this.exportAction(workflowId, segmentId, stepId, actionId);
    }
  }

  /** Export a live action by its RefId path within the workflow (the canvas surfaces RefIds, not PKs). */
  private exportAction(
    workflowId: number,
    segmentId: number,
    stepId: number,
    actionId: number
  ) {
    this.getService
      .apiV1WorkflowWorkflowIdStepStepRefIdActionActionRefIdGet(
        workflowId,
        stepId,
        actionId
      )
      .subscribe({
        next: action => {
          if (!action?.id) return;
          this.templateTransfer
            .exportActionByRef(workflowId, segmentId, stepId, actionId)
            .subscribe({
              next: blob =>
                downloadBlob(
                  blob,
                  buildTemplateFileName('Action', action.name)
                ),
              error: async (err: unknown) =>
                this.messageService?.add({
                  severity: 'error',
                  summary: 'Export failed',
                  detail: await extractBlobErrorMessage(err),
                }),
            });
        },
        error: (err: unknown) =>
          this.messageService?.add({
            severity: 'error',
            summary: 'Export failed',
            detail:
              (err as { message?: string })?.message ??
              'Could not load the action to export.',
          }),
      });
  }
}
